import { useCallback, useState } from "react";

import {
  SignupKeysService,
  type CreateSignupKeyRequest,
  type SignupKeyCreatedResponse,
  type UserRole,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (key: SignupKeyCreatedResponse) => void;
}

const ROLES: ReadonlyArray<{ value: UserRole; label: string; help: string }> = [
  {
    value: "member",
    label: "Anggota",
    help: "Akses standar — dapat memakai gateway tapi tidak mengubah governance config.",
  },
  {
    value: "viewer",
    label: "Pengamat",
    help: "Read-only — cocok untuk auditor / observer eksternal.",
  },
  {
    value: "admin",
    label: "Admin",
    help: "Full akses tenant: kelola policy / asset / risk + undang anggota.",
  },
];

const EXPIRY_OPTIONS: ReadonlyArray<{ days: number; label: string }> = [
  { days: 1, label: "24 jam" },
  { days: 7, label: "7 hari (default)" },
  { days: 30, label: "30 hari" },
  { days: 90, label: "90 hari" },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteCreateDialog({ open, onClose, onCreated }: Props) {
  const [role, setRole] = useState<UserRole>("member");
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");

  const mutator = useCallback(
    (body: CreateSignupKeyRequest) => SignupKeysService.create(body),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<
    CreateSignupKeyRequest,
    SignupKeyCreatedResponse
  >(mutator, {
    onSuccess: (data) => {
      onCreated(data);
      setRole("member");
      setExpiresInDays(7);
      setNote("");
      setEmail("");
    },
  });

  const handleClose = () => {
    reset();
    setRole("member");
    setExpiresInDays(7);
    setNote("");
    setEmail("");
    onClose();
  };

  const trimmedEmail = email.trim();
  const hasEmail = trimmedEmail.length > 0;
  const emailValid = !hasEmail || EMAIL_PATTERN.test(trimmedEmail);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasEmail && !emailValid) return;
    const payload: CreateSignupKeyRequest = {
      default_role: role,
      expires_in_days: expiresInDays,
    };
    const trimmedNote = note.trim();
    if (trimmedNote) payload.note = trimmedNote;
    if (hasEmail) payload.email = trimmedEmail;
    await mutate(payload);
  };

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>Undang anggota baru</DialogTitle>
        <DialogDescription>
          Buat signup key untuk dipakai calon anggota saat registrasi.
          Kode plaintext ditampilkan satu kali setelah dibuat.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div>
            <div className="label-eyebrow mb-2">Peran calon anggota</div>
            <div
              role="radiogroup"
              aria-label="Peran"
              className="space-y-2"
            >
              {ROLES.map((opt) => {
                const checked = opt.value === role;
                return (
                  <label
                    key={opt.value}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors",
                      checked
                        ? "border-ocean-300 bg-ocean-50"
                        : "border-ocean-100 hover:border-ocean-200"
                    )}
                  >
                    <input
                      type="radio"
                      name="invite-role"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setRole(opt.value)}
                      className="mt-0.5 h-4 w-4 border-ocean-200 text-ocean-600 focus:ring-ocean-400"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-ocean-900">
                        {opt.label}
                      </div>
                      <div className="mt-0.5 text-xs text-neutral-600">
                        {opt.help}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label
              htmlFor="invite-expiry"
              className="label-eyebrow mb-1 block"
            >
              Masa berlaku
            </label>
            <select
              id="invite-expiry"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              className={cn(
                "h-10 w-full rounded-md border border-ocean-100 bg-white px-3 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-ocean-400"
              )}
            >
              {EXPIRY_OPTIONS.map((opt) => (
                <option key={opt.days} value={opt.days}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <FormField
            label="Catatan (opsional)"
            value={note}
            onChange={setNote}
            maxLength={200}
            placeholder="Mis. onboarding tim treasury Q3"
            hint="Hanya admin yang melihat catatan ini. Tidak disertakan ke email signup."
          />

          <FormField
            label="Email pengundang (opsional)"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="user@perusahaan.co.id"
            error={
              hasEmail && !emailValid
                ? "Format email tidak valid."
                : undefined
            }
            hint="Jika diisi, Monago akan mengirim invitation email dengan kode signup ke alamat ini."
            autoComplete="email"
          />

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Batal
          </Button>
          <Button type="submit" disabled={isLoading || (hasEmail && !emailValid)}>
            {isLoading
              ? hasEmail
                ? "Mengirim..."
                : "Membuat..."
              : hasEmail
                ? "Kirim undangan"
                : "Buat signup key"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
